import React, { useEffect, useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Shield, Zap, Users, FileText, AlertTriangle, Info, Building2, Award } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const PLAN_LABELS = {
  start: { name: 'Start', price: '8 900 kr/mån', color: 'bg-gray-100' },
  growth: { name: 'Growth', price: '16 900 kr/mån', color: 'bg-primary/10' },
  scale: { name: 'Scale', price: '29 900 kr/mån', color: 'bg-primary/20' },
  enterprise: { name: 'Enterprise', price: 'Offert', color: 'bg-secondary/10' },
};

const STATUS_LABELS = {
  active: 'Aktiv',
  suspended: 'Pausad',
  expired: 'Utgången',
};

const BLOCK_LABELS = {
  no_active_subscription: 'Ingen aktiv prenumeration',
  subscription_expired: 'Prenumerationen har löpt ut',
  subscription_suspended: 'Prenumerationen är pausad',
  grace_period_active: 'Anståndsperiod aktiv',
};

const SubscriptionPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      const resp = await api.get('/org/subscription');
      setData(resp.data);
    } catch (err) {
      toast.error('Kunde inte hämta prenumerationsinformation');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Laddar...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!data?.has_subscription) {
    return (
      <DashboardLayout>
        <div className="space-y-8" data-testid="subscription-page">
          <h1 className="text-4xl font-heading font-black tracking-tight">Prenumeration</h1>
          <Card className="border-2">
            <CardContent className="p-12 text-center">
              <Shield className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-2xl font-heading font-bold mb-2">Ingen aktiv prenumeration</h2>
              <p className="text-muted-foreground">Kontakta din administratör för att aktivera din plan.</p>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  const sub = data.subscription;
  const plan = PLAN_LABELS[sub.plan] || PLAN_LABELS.start;
  const features = data.plan_features;
  const aiUsage = data.ai_usage;
  const limits = data.limits || {};
  const ocn = data.ocn || {};
  const graceUntil = data.grace_until;
  const blockReason = data.block_reason;

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="subscription-page">
        <div>
          <h1 className="text-4xl font-heading font-black tracking-tight">Prenumeration</h1>
          <p className="text-muted-foreground mt-2">Översikt över din organisations prenumeration.</p>
        </div>

        {/* Info banner */}
        <div
          className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg"
          data-testid="info-banner"
        >
          <Info className="w-5 h-5 text-blue-500 mt-0.5 shrink-0" />
          <p className="text-sm text-blue-800">
            Kontakta din administratör för att ändra plan eller tillägg.
          </p>
        </div>

        {/* Block reason */}
        {blockReason && blockReason !== 'grace_period_active' && (
          <div
            className="flex items-start gap-3 p-4 bg-destructive/10 border border-destructive/30 rounded-lg"
            data-testid="block-reason"
          >
            <AlertTriangle className="w-5 h-5 text-destructive mt-0.5 shrink-0" />
            <p className="text-sm text-destructive font-medium">
              {BLOCK_LABELS[blockReason] || blockReason}
            </p>
          </div>
        )}

        {/* Grace period */}
        {graceUntil && (
          <div
            className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg"
            data-testid="grace-banner"
          >
            <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 shrink-0" />
            <p className="text-sm text-yellow-800">
              Anståndsperiod aktiv till {new Date(graceUntil).toLocaleDateString('sv-SE')}.
              Kontakta din administratör för att förnya prenumerationen.
            </p>
          </div>
        )}

        {/* Plan overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="border-2">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className={`p-3 rounded-lg ${plan.color}`}>
                  <Shield className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Aktuell plan</p>
                  <p className="text-2xl font-heading font-black">{plan.name}</p>
                </div>
              </div>
              <p className="text-lg font-bold">{plan.price}</p>
              <p className="text-sm text-muted-foreground mt-1">
                Status: <span className={`font-bold ${sub.status === 'active' ? 'text-success' : 'text-destructive'}`}>
                  {STATUS_LABELS[sub.status] || sub.status}
                </span>
              </p>
            </CardContent>
          </Card>

          <Card className="border-2">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 rounded-lg bg-blue-50">
                  <Users className="w-6 h-6 text-blue-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Användare</p>
                  <p className="text-2xl font-heading font-black">{limits.max_users || '—'}</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">max i din plan</p>
            </CardContent>
          </Card>

          <Card className="border-2">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 rounded-lg bg-primary/10">
                  <Zap className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">AI-användning</p>
                  <p className="text-2xl font-heading font-black">
                    {aiUsage?.used || 0}/{aiUsage?.limit || 0}
                  </p>
                </div>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${
                    aiUsage?.exceeded ? 'bg-destructive' : 'bg-primary'
                  }`}
                  style={{ width: `${Math.min(100, ((aiUsage?.used || 0) / (aiUsage?.limit || 1)) * 100)}%` }}
                />
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                {aiUsage?.remaining || 0} förfrågningar kvar denna månad
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Limits */}
        <Card className="border-2" data-testid="limits-card">
          <CardHeader className="border-b pb-4">
            <CardTitle className="font-heading font-bold">Begränsningar</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-muted-foreground shrink-0" />
                <div>
                  <p className="text-sm text-muted-foreground">Max användare</p>
                  <p className="font-bold" data-testid="limit-users">{limits.max_users || '—'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-muted-foreground shrink-0" />
                <div>
                  <p className="text-sm text-muted-foreground">Max aktiva case/år</p>
                  <p className="font-bold" data-testid="limit-cases">{limits.max_cases_per_year || '—'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Building2 className="w-5 h-5 text-muted-foreground shrink-0" />
                <div>
                  <p className="text-sm text-muted-foreground">Max verksamheter</p>
                  <p className="font-bold" data-testid="limit-verksamheter">{limits.max_verksamheter || '—'}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* OCN status */}
        <Card className="border-2" data-testid="ocn-card">
          <CardHeader className="border-b pb-4">
            <CardTitle className="font-heading font-bold">OCN-validering</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Award className="w-5 h-5 text-muted-foreground shrink-0" />
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <p className="font-bold" data-testid="ocn-status">
                  {ocn.enabled ? (
                    <span className="text-success">
                      Aktiv {ocn.source && <span className="text-muted-foreground font-normal">({ocn.source === 'paket' ? 'Ingår i paket' : 'Aktiverad som tillägg'})</span>}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">Inaktiv</span>
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Period info */}
        <Card className="border-2">
          <CardHeader className="border-b pb-4">
            <CardTitle className="font-heading font-bold">Perioddetaljer</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-muted-foreground">Startdatum</p>
                <p className="font-bold">{sub.period_start ? new Date(sub.period_start).toLocaleDateString('sv-SE') : '—'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Slutdatum</p>
                <p className="font-bold">{sub.period_end ? new Date(sub.period_end).toLocaleDateString('sv-SE') : 'Löpande'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Faktureringskälla</p>
                <p className="font-bold capitalize">{sub.billing_source}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Features */}
        <Card className="border-2">
          <CardHeader className="border-b pb-4">
            <CardTitle className="font-heading font-bold">Inkluderade funktioner</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {features?.features?.map((feature) => (
                <div key={feature} className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                  <FileText className="w-4 h-4 text-success flex-shrink-0" />
                  <span className="text-sm font-medium capitalize">{feature.replace(/_/g, ' ')}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default SubscriptionPage;
